import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { CheckCircle, RefreshCw, Smartphone, X } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import useDialog from '../../hooks/useDialog';
import { formatClinicTime } from '../../utils/clinicTime';

// Punching from the desk, finishing on the phone. A desktop has no camera and its position is a
// guess from the network, so the code hands the punch to the one device that can do both properly.
//
// The code is the key: whoever scans it within three minutes punches as this person, with no
// sign-in. That is deliberate (owner, 2026-10-03) and the selfie is what makes it safe: a punch by
// somebody else carries the wrong face, and an out-of-range one is flagged for review anyway.
//
// While it is open the dialog watches the person's own attendance; when the phone completes the
// punch, the server knows before the browser does, so this closes itself on the next look.

const POLL_MS = 3000;

/**
 * Seconds until a deadline held as a browser timestamp, never below zero.
 *
 * Deliberately NOT worked out from the server's `expiresAt`: that is a naive clinic-time string, and
 * `new Date()` would read it as the browser's own zone. On a machine five hours behind the clinic a
 * three minute code read as nine hours, which is what the first run showed. The server also sends
 * `expiresInSeconds`, so the deadline is fixed once from that and counted down locally.
 */
export const secondsUntil = (deadlineMs, now = Date.now()) =>
  Math.max(0, Math.round((deadlineMs - now) / 1000));

export const countdownLabel = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const PunchCodeDialog = ({ side, forUser = null, onDone, onClose }) => {
  const [code, setCode] = useState(null); // { token, path, expiresAt, expiresInSeconds }
  const [image, setImage] = useState(null);
  const [left, setLeft] = useState(0);
  const deadline = useRef(0);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // the entry once the phone has punched
  const dialogRef = useDialog(() => onClose(), { confirmDiscard: false });

  const verb = side === 'OUT' ? 'Punch out' : 'Punch in';
  const title = forUser ? `${verb} for ${forUser.name}` : `${verb} with your phone`;

  const load = useCallback(async () => {
    setError('');
    setImage(null);
    try {
      const res = await attendanceAPI.punchCode(side, forUser && {
        forUserId: forUser.id,
        reason: forUser.reason,
        reasonNote: forUser.reasonNote,
      });
      setCode(res);
      deadline.current = Date.now() + (res.expiresInSeconds ?? 180) * 1000;
      setLeft(secondsUntil(deadline.current));
      // The phone has to reach the same host the person is already using, which only the browser
      // knows: the server never sees the name staff type to get here.
      const url = `${window.location.origin}${res.path}`;
      const dataUrl = await QRCode.toDataURL(url, { width: 480, margin: 1, errorCorrectionLevel: 'M' });
      setImage({ dataUrl, url });
    } catch (e) {
      setError(e?.message || 'The code could not be created.');
    }
  }, [side, forUser]);

  useEffect(() => { load(); }, [load]);

  // The countdown, and the code dying when it runs out.
  useEffect(() => {
    if (!code || done) return undefined;
    const t = setInterval(() => {
      const remaining = secondsUntil(deadline.current);
      setLeft(remaining);
      if (remaining <= 0) setImage(null);
    }, 1000);
    return () => clearInterval(t);
  }, [code, done]);

  // Watch for the phone finishing. The punch itself happens on the phone, so nothing here submits.
  // A spent code is the signal, and it works whoever the code belongs to: this person's own
  // attendance would say nothing about a colleague's.
  // Once the code has run out there is nothing left to watch, so the polling stops with it. Left
  // running it would keep asking about a dead code for as long as the screen stayed open.
  useEffect(() => {
    if (!code || done || left <= 0) return undefined;
    const tick = async () => {
      try {
        const status = await attendanceAPI.punchCodeStatus(code.token);
        if (status?.valid === false && status.reason === 'USED') {
          // Only your own page can say at what time. For a colleague, that it happened at all is
          // the whole of what this screen is entitled to know.
          if (forUser) {
            setDone({});
          } else {
            const mine = await attendanceAPI.mine().catch(() => null);
            setDone(mine?.open || mine?.today || {});
          }
        }
      } catch {
        // A failed look is not worth showing: the next one is three seconds away.
      }
    };
    const t = setInterval(tick, POLL_MS);
    tick();
    return () => clearInterval(t);
    // `left` is deliberately not a dependency: it ticks every second and would rebuild the interval
    // each time. `deadDue` flips once, which is the only change that matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, done, forUser, left <= 0]);

  const expired = left <= 0 && !done;

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Smartphone className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold text-white">{title}</h2>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-center">
          {done ? (
            <div className="py-6 space-y-2">
              <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto" aria-hidden="true" />
              <p className="text-lg font-semibold text-gray-900">
                {side === 'OUT' ? 'Punched out' : 'Punched in'}
                {!forUser && (done.clockInAt || done.clockOutAt)
                  && ` at ${formatClinicTime(done.clockOutAt || done.clockInAt, { hour: 'numeric', minute: '2-digit', hour12: true }, '')}`}
              </p>
              <p className="text-sm text-gray-600">
                {forUser ? `${forUser.name} did it on their phone.` : 'Done on your phone.'}
              </p>
            </div>
          ) : error ? (
            <div className="py-6 space-y-3">
              <p className="text-sm text-red-700">{error}</p>
              <button type="button" onClick={load} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                Try again
              </button>
            </div>
          ) : expired ? (
            <div className="py-6 space-y-3">
              <p className="text-sm text-gray-700">The code has expired. A code lasts three minutes.</p>
              <button type="button" onClick={load} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                <RefreshCw className="w-4 h-4" aria-hidden="true" /> Show a new code
              </button>
            </div>
          ) : image ? (
            <>
              <div className="mx-auto w-56 h-56 rounded-xl border border-gray-200 p-2 bg-white">
                <img src={image.dataUrl} alt="Code to scan with your phone camera" className="w-full h-full" />
              </div>
              <p className="text-sm text-gray-700">
                {forUser
                  ? `Let ${forUser.name} point their phone camera at this code. The form opens on their phone, takes their photo and their position, and records the time.`
                  : 'Open the camera on your phone and point it at this code. The form opens on your phone, takes your photo and your position, and records the time.'}
              </p>
              <p className="text-sm text-gray-500">
                Valid for <span className="font-semibold text-gray-900 tabular-nums">{countdownLabel(left)}</span>
              </p>
              <p className="urdu-text text-sm leading-loose text-gray-700" dir="rtl">
                {forUser ? 'یہ کوڈ اُن کے فون کے کیمرے سے اسکین کروائیں۔ فارم اُن کے فون پر کھلے گا۔' : 'اپنے فون کے کیمرے سے یہ کوڈ اسکین کریں۔ فارم آپ کے فون پر کھلے گا۔'}
              </p>
            </>
          ) : (
            <div className="py-10">
              <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin mx-auto mb-2" aria-hidden="true" />
              <p className="text-sm text-gray-500">Making a code…</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end rounded-b-2xl">
          <button
            type="button"
            onClick={done ? onDone : onClose}
            className={`px-5 py-2 rounded-lg text-sm font-medium ${done ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            {done ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PunchCodeDialog;
